/**
 * Reusable Checkbox Component
 * Built with Tailwind CSS and proper accessibility
 */

import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      error,
      helperText,
      className,
      fullWidth = false,
      ...props
    },
    ref
  ) => {
    const checkboxClasses = clsx(
      'w-4 h-4 text-primary-600 border-gray-300 rounded',
      'focus:ring-2 focus:ring-primary-500 focus:ring-offset-0',
      'transition-colors duration-200',
      'disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed',
      {
        'border-red-300': error,
      },
      className
    );

    return (
      <div className={clsx({ 'w-full': fullWidth })}>
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              ref={ref}
              type="checkbox"
              className={checkboxClasses}
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={
                error ? `${props.id}-error` : helperText ? `${props.id}-helper` : undefined
              }
              {...props}
            />
          </div>
          
          {label && (
            <div className="ml-3 text-sm">
              <label
                htmlFor={props.id}
                className={clsx(
                  'font-medium cursor-pointer',
                  {
                    'text-red-700': error,
                    'text-gray-700': !error,
                  }
                )}
              >
                {label}
                {props.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            </div>
          )}
        </div>
        
        {error && (
          <p
            id={`${props.id}-error`}
            className="mt-1 text-sm text-red-600 ml-7"
            role="alert"
          >
            {error}
          </p>
        )}
        
        {helperText && !error && (
          <p
            id={`${props.id}-helper`}
            className="mt-1 text-sm text-gray-500 ml-7"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;

